/* eslint-disable */
import { useState, useEffect } from "react";
import { css } from "./shared";
import { Nav, Home, ProductPage, UtilityBar } from "./pages/SiteMain";
import { AboutPage, AccountantsPage, FundabilityLanding, ContactPage, LegalPage } from "./pages/SitePages";
import { Questionnaire, LeadCapture, AILoading } from "./pages/Flow";
import { Results, Admin, AdminLogin, Footer } from "./pages/ResultsAdmin";
import { Calculator } from "./pages/Calculator";
import { InsightsIndex, ArticlePage } from "./pages/Insights";
import { articles } from "./content/articles";

// ─── APP ROOT ─────────────────────────────────────────────────────────────────

// ─── ROUTES & SEO ─────────────────────────────────────────────────────────────
// Real URLs + unique title/description per page (what Google indexes and shows).
const ROUTES = {
  home:        ["/", "CapitalOS | UK Commercial Finance Broker for Ambitious SMEs", "CapitalOS is a UK commercial finance broker helping ambitious SMEs access business loans, asset finance, invoice finance, equipment and vehicle finance — whole-of-market, relationship-led. Get your free AI-powered Fundability Score."],
  about:       ["/about", "About CapitalOS | UK Commercial Finance Broker, Founded by Nick Pope", "Meet Nick Pope, founder of CapitalOS — a UK commercial finance broker with 20+ years of relationship-led experience helping ambitious businesses access funding, structure opportunities and grow with confidence."],
  equipment:   ["/equipment-finance", "Equipment Finance £10k–£2m | CapitalOS", "Fund machinery, technology and business assets. VAT deferrals, no deposit, payments 100% tax deductible, terms 1–7 years. Fast, no-obligation quotes."],
  asset:       ["/asset-finance", "Asset Finance £10k–£2m | CapitalOS", "Release cash from assets you already own, or fund new plant, machinery and equipment. Terms 1–7 years, fast decisions, whole-of-market access."],
  vehicle:     ["/vehicle-finance", "Vehicle & Fleet Finance | CapitalOS", "Finance cars, vans, HGVs and entire fleets. Hire Purchase, Finance Lease or Contract Hire. EV and hybrid options. Preserve working capital."],
  invoice:     ["/invoice-finance", "Invoice Finance — Up to 90% Advance | CapitalOS", "Unlock cash tied up in unpaid invoices within 24–48 hours. Factoring, discounting, selective and confidential facilities for UK businesses."],
  loans:       ["/business-loans", "Business Loans £10k–£2m | CapitalOS", "Flexible business loans with no security required under £250k. Terms 1–7 years, fixed repayments, repay early with no penalties."],
  development: ["/development-finance", "Development Finance £100k–£25m+ | CapitalOS", "Fund residential and commercial property developments. Staged drawdowns, interest rolled up, terms built around your exit strategy."],
  green:       ["/green-energy-finance", "Green Energy Finance | CapitalOS", "Finance solar, battery storage, EV charging, heat pumps and LED. Energy savings help offset repayments. £10k–£2m+ for UK businesses."],
  merchant:    ["/merchant-cash-advance", "Merchant Cash Advance £5k–£500k | CapitalOS", "Funding based on your card sales. Repayments flex with daily takings — ideal for retail, hospitality and leisure businesses."],
  refinance:   ["/refinancing", "Business Refinancing & Debenture Clearance | CapitalOS", "Restructure expensive or restrictive facilities. Clear bank charges, free up the balance sheet, sale and leaseback options."],
  accountants: ["/for-accountants", "Accountant Referral Programme | CapitalOS", "Help your clients access funding and earn referral income. Simple process, client relationships remain yours, fast decisions in principle."],
  fundability: ["/fundability-score", "Free Fundability Score — 5-Minute Business Finance Diagnostic | CapitalOS", "AI-powered commercial finance diagnostic for UK SMEs. See where cash is trapped, which finance routes fit and what to prepare. Free, instant, no obligation."],
  calculator: ["/finance-calculator", "Commercial Finance Calculator | CapitalOS", "Get an indicative business loan, asset finance, invoice finance or borrowing power figure in under a minute."],
  insights:    ["/insights", "Commercial Intelligence | CapitalOS", "Guides, research and commercial thinking for ambitious UK businesses."],
  contact:     ["/contact", "Book a Capital Review | CapitalOS", "A focused 20-minute conversation about your funding position — not a sales call. Fast, no-obligation response within one business day."],
  privacy:     ["/privacy-policy", "Privacy Policy | CapitalOS", "How CapitalOS collects, uses and protects your personal data under UK GDPR."],
  terms:       ["/terms-and-conditions", "Terms & Conditions | CapitalOS", "Terms governing the CapitalOS website, Fundability Score tool and brokerage services."],
  questionnaire: ["/fundability-score/assessment", "Fundability Assessment | CapitalOS", "Answer 26 questions to generate your Fundability Score report."],
  lead:        ["/fundability-score/details", "Your Details | CapitalOS", "Enter your details to receive your Fundability Score report."],
  results:     ["/fundability-score/report", "Your Fundability Report | CapitalOS", "Your personalised commercial finance diagnostic."],
  admin:       ["/admin", "Admin | CapitalOS", "Administrator access."],
};

const PATHS = Object.fromEntries(Object.entries(ROUTES).map(([k, [p]]) => [p, k]));

// Dynamic /insights/:slug articles live outside the fixed ROUTES lookup. This check runs
// before PATHS is consulted — it only intercepts paths starting with "/insights/" followed
// by something, so it can never collide with the fixed "/insights" index route (which is
// shorter and falls through to the normal PATHS lookup exactly like every other route).
function parsePath(pathname) {
  if (pathname.startsWith("/insights/") && pathname.length > "/insights/".length) {
    const slug = pathname.slice("/insights/".length).replace(/\/$/, "");
    if (slug) return { page: "article", slug };
  }
  return { page: PATHS[pathname] || "home", slug: null };
}

function applyMeta(page, article) {
  try {
    if (page === "article" && article) {
      document.title = `${article.title} | CapitalOS`;

      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
      }
      m.setAttribute("content", article.description);

      let c = document.querySelector('link[rel="canonical"]');
      if (!c) {
        c = document.createElement("link");
        c.setAttribute("rel", "canonical");
        document.head.appendChild(c);
      }
      c.setAttribute("href", "https://www.capital-os.co.uk/insights/" + article.slug);
      return;
    }

    const r = ROUTES[page] || ROUTES.home;
    document.title = r[1];

    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute("content", r[2]);

    let c = document.querySelector('link[rel="canonical"]');
    if (!c) {
      c = document.createElement("link");
      c.setAttribute("rel", "canonical");
      document.head.appendChild(c);
    }
    c.setAttribute("href", "https://www.capital-os.co.uk" + r[0]);
  } catch (e) {}
}

export default function App() {
  const [page, setPage] = useState(() => {
    try {
      return parsePath(window.location.pathname).page;
    } catch (e) {
      return "home";
    }
  });
  const [articleSlug, setArticleSlug] = useState(() => {
    try {
      return parsePath(window.location.pathname).slug;
    } catch (e) {
      return null;
    }
  });

  const [answers, setAnswers] = useState({});
  const [report, setReport] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [adminAuthed, setAdminAuthed] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const article = page === "article" ? articles.find((a) => a.slug === articleSlug) : null;
    applyMeta(page, article);
  }, [page, articleSlug]);

  useEffect(() => {
    const onPop = () => {
      try {
        const parsed = parsePath(window.location.pathname);
        setPage(parsed.page);
        setArticleSlug(parsed.slug);
      } catch (e) {}
    };

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const prodPages = [
    "equipment",
    "asset",
    "vehicle",
    "invoice",
    "loans",
    "development",
    "green",
    "merchant",
    "refinance",
  ];

  const bare = ["questionnaire", "lead", "loading"].includes(page);

  const setPageSafe = (p) => {
    setPage(p);
    setArticleSlug(null);

    try {
      const r = ROUTES[p];
      if (r && window.location.pathname !== r[0]) {
        window.history.pushState({}, "", r[0]);
      }
    } catch (e) {}
  };

  const goToArticle = (slug) => {
    setPage("article");
    setArticleSlug(slug);

    try {
      const path = "/insights/" + slug;
      if (window.location.pathname !== path) {
        window.history.pushState({}, "", path);
      }
    } catch (e) {}
  };

  return (
    <>
      <style>{css}</style>

      {!bare && <UtilityBar />}
      {!bare && <Nav page={page} setPage={setPageSafe} scrolled={scrolled} />}

      {page === "home" && <Home setPage={setPageSafe} />}
      {page === "about" && <AboutPage setPage={setPageSafe} />}
      {prodPages.includes(page) && <ProductPage product={page} setPage={setPageSafe} />}

      {page === "accountants" && <AccountantsPage setPage={setPageSafe} />}
      {page === "fundability" && (
        <FundabilityLanding onStart={() => setPageSafe("questionnaire")} setPage={setPageSafe} />
      )}
      {page === "calculator" && <Calculator setPage={setPageSafe} />}
      {page === "insights" && <InsightsIndex setPage={setPageSafe} onOpenArticle={goToArticle} />}
      {page === "article" && <ArticlePage slug={articleSlug} setPage={setPageSafe} />}
      {page === "contact" && <ContactPage setPage={setPageSafe} />}
      {page === "privacy" && <LegalPage type="privacy" setPage={setPageSafe} />}
      {page === "terms" && <LegalPage type="terms" setPage={setPageSafe} />}

      {page === "questionnaire" && (
        <Questionnaire
          onComplete={(a) => {
            setAnswers(a);
            setPageSafe("lead");
          }}
          setPage={setPageSafe}
        />
      )}

      {page === "lead" && (
        <LeadCapture
          answers={answers}
          onComplete={(a) => {
            setAnswers(a);
            setPageSafe("loading");
          }}
          setPage={setPageSafe}
        />
      )}

      {page === "loading" && (
        <AILoading
          answers={answers}
          onComplete={(d) => {
            setReport({ ...d, answers });
            setPageSafe("results");
          }}
        />
      )}

      {page === "results" && report && <Results data={report} setPage={setPageSafe} />}

      {page === "admin" &&
        (adminAuthed ? (
          <Admin currentLead={report} setPage={setPageSafe} />
        ) : (
          <AdminLogin onSuccess={() => setAdminAuthed(true)} setPage={setPageSafe} />
        ))}

      {!bare && <Footer setPage={setPageSafe} />}
    </>
  );
}
